import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Dialog, IconButton, Stack, Typography } from '@mui/material';
import { LinkedinTypes, ProductGroups } from 'enums';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { CountryCode, LinkedinAccount } from '../linkedin-accounts.types';
import { AppSyncErrors, AppSyncSteps, countedStepCount, stepOrder, SyncProcessingStatuses } from './app-sync-dialog.types';
import EnterAccountInfo from './enter-account-info';
import SelectLocationStep from './select-location-step';
import SelectProductStep from './select-product-step';
import StepHint from './step-hint';
import StepProgress from './step-progress';
import SyncError from './sync-error/sync-error';
import SyncProcessing from './sync-processing/sync-processing';
import SyncSuccessful from './sync-successful';

interface Props {
  open: boolean;
  product: ProductGroups;
  isStarterExperience: boolean;
  activeStepIndex: number;
  loading: {
    create: boolean;
    sync: boolean;
    verifyPin: boolean;
    getCaptcha: boolean;
    solveCaptcha: boolean;
    getThirdPartyScreenshot: boolean;
    thirdPartyClick: boolean;
    update: boolean;
  };
  syncProcessingStatus: SyncProcessingStatuses;
  syncError: AppSyncErrors | null;

  selectedLinkedinProduct: LinkedinTypes;
  allCountryCodes: CountryCode[];
  selectedCountry: OptionType;
  selectedCity: OptionType;

  userName: string;
  password: string;

  linkedinAccount: LinkedinAccount | null;
  verificationCode: string;
  twoFaScreenshot?: string | null;
  captcha: string | null;
  markerPosition: { x: number; y: number; percentageX: number; percentageY: number } | null;
  thirdPartyScreenshot: string | null;
  thirdPartyText: string;
  linkedinContracts: string[];
  selectedContract: string | null;

  increaseActiveStepIndex(): void;

  decreaseActiveStepIndex(): void;

  handleLinkedinProductChange(value: string): () => void;

  handleCountryChange(event: React.ChangeEvent<any>, newValue: any): void;

  handleCityChange(event: React.ChangeEvent<any>, newValue: any): void;

  handleUserNameChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handlePasswordChange(event: React.ChangeEvent<HTMLInputElement>): void;

  moveToCampaignBuilder?(): void;

  handleCreateAndSynchronizeLiAccount(): void;

  handleVerificationCodeChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleVerifyLinkedinAccountFromAppSync(mode?: 'pin' | 'approved'): void;

  handleImageClick(event: React.MouseEvent): void;

  handleDeleteMarker(event: React.MouseEvent): void;

  handleSolveCaptcha(): void;

  handleThirdPartyClick(): void;

  handleThirdPartyTextChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSetThirdPartyTextFromAppSync(): void;

  handleSelectContract(contract: string): () => void;

  handleAddContractFromAppSync(): void;

  handleEditAndSynchronizeLiAccount(): void;

  handleReSynchronizeLiAccount(): void;

  onResetAccount?(): void;

  handleOpenEditLinkedinAccountNameDialog?(account: LinkedinAccount): () => void;

  handleClose(): void;

  selectedLicenseId: string | null;
  handleLicenseSelect(licenseId: string | null): void;
  isLogoutMode?: boolean;
}

/**
 * Kurzzeichen fürs Konto, wie es der Support zitiert: „#A7F3-9C · Claudia Künzl".
 *
 * Der Kunde soll bei einem unklaren Fehler etwas nennen können, das das Konto eindeutig
 * benennt, ohne eine 24-stellige Objekt-ID abzutippen.
 */
const createReferenceLabel = (account: LinkedinAccount | null): string | undefined => {
  if (_.isNil(account)) return undefined;

  const id = (account._id || '').toUpperCase();
  const short = id.slice(-6);
  const name = [account.firstName, account.lastName].filter(Boolean).join(' ') || account.userEmail;

  if (!short) return name || undefined;

  const reference = `#${short.slice(0, 4)}-${short.slice(4)}`;
  return name ? `${reference} · ${name}` : reference;
};

const AppSyncDialog: React.FC<Props> = ({
  open,
  product,
  isStarterExperience,
  isLogoutMode = false,
  activeStepIndex,
  loading,
  syncProcessingStatus,
  syncError,
  selectedLinkedinProduct,
  allCountryCodes,
  selectedCountry,
  selectedCity,
  userName,
  password,
  linkedinAccount,
  verificationCode,
  twoFaScreenshot,
  captcha,
  markerPosition,
  thirdPartyScreenshot,
  thirdPartyText,
  linkedinContracts,
  selectedContract,
  increaseActiveStepIndex,
  decreaseActiveStepIndex,
  handleLinkedinProductChange,
  handleCountryChange,
  handleCityChange,
  handleUserNameChange,
  handlePasswordChange,
  moveToCampaignBuilder,
  handleCreateAndSynchronizeLiAccount,
  handleVerificationCodeChange,
  handleVerifyLinkedinAccountFromAppSync,
  handleImageClick,
  handleDeleteMarker,
  handleSolveCaptcha,
  handleThirdPartyClick,
  handleThirdPartyTextChange,
  handleSetThirdPartyTextFromAppSync,
  handleSelectContract,
  handleAddContractFromAppSync,
  handleEditAndSynchronizeLiAccount,
  handleReSynchronizeLiAccount,
  onResetAccount,
  handleOpenEditLinkedinAccountNameDialog,
  handleClose,
  selectedLicenseId,
  handleLicenseSelect,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const activeStep: AppSyncSteps = stepOrder[activeStepIndex] || AppSyncSteps.SYNC_ERROR;

  const userEmail = linkedinAccount?.userEmail;
  const userFirstName = linkedinAccount?.firstName;
  const syncStage = linkedinAccount?.syncStage;
  const statusMessage = linkedinAccount?.statusMessage;
  const liProductType = linkedinAccount?.liProductType || selectedLinkedinProduct || LinkedinTypes.LINKEDIN;

  const locationLabel = [selectedCity?.label, selectedCountry?.label].filter(Boolean).join(', ') || undefined;

  // Fortschritt zählt nur die vier Schritte des Assistenten. Fertig und Fehler tragen
  // keine Nummer — dort ist nichts mehr abzuarbeiten.
  const showProgress = activeStepIndex >= 0 && activeStepIndex < countedStepCount && !isLogoutMode;

  const createContent = () => {
    switch (activeStep) {
      case AppSyncSteps.ASSIGN_LICENSE:
        return (
          <SelectProductStep
            selectedLinkedinProduct={selectedLinkedinProduct}
            selectedLicenseId={selectedLicenseId}
            handleLinkedinProductChange={handleLinkedinProductChange}
            onLicenseSelect={handleLicenseSelect}
            // Die Lizenz legt nichts mehr an — sie wählt nur den Sitz, auf dem das Konto
            // gleich laufen wird. Angelegt wird am Ende des Standort-Schrittes, in einem
            // Aufruf, der die `licenseId` mitschickt.
            onNext={increaseActiveStepIndex}
            onCancel={!isStarterExperience ? handleClose : undefined}
          />
        );
      case AppSyncSteps.ENTER_ACCOUNT_INFO:
        return (
          <EnterAccountInfo
            userName={userName}
            password={password}
            handleUserNameChange={handleUserNameChange}
            handlePasswordChange={handlePasswordChange}
            onBack={decreaseActiveStepIndex}
            onNext={increaseActiveStepIndex}
          />
        );
      case AppSyncSteps.SELECT_LOCATION:
        return (
          <SelectLocationStep
            loading={loading.create}
            allCountryCodes={allCountryCodes}
            selectedCountry={selectedCountry}
            selectedCity={selectedCity}
            handleCountryChange={handleCountryChange}
            handleCityChange={handleCityChange}
            onBack={decreaseActiveStepIndex}
            onStartLogin={handleCreateAndSynchronizeLiAccount}
          />
        );
      case AppSyncSteps.SYNCHRONIZING:
        return (
          <SyncProcessing
            isStarterExperience={isStarterExperience}
            isLogoutMode={isLogoutMode}
            syncStage={syncStage}
            statusMessage={statusMessage}
            status={syncProcessingStatus}
            liProductType={liProductType}
            locationLabel={locationLabel}
            onResetAccount={isLogoutMode ? undefined : onResetAccount}
            verificationCode={verificationCode}
            verificationLoading={loading.verifyPin}
            twoFaScreenshot={twoFaScreenshot}
            captcha={captcha}
            markerPosition={markerPosition}
            getCaptchaLoading={loading.getCaptcha}
            solveCaptchaLoading={loading.solveCaptcha}
            thirdPartyScreenshot={thirdPartyScreenshot}
            thirdPartyText={thirdPartyText}
            thirdPartyTextPlaceholder={linkedinAccount?.thirdPartyTextPlaceholder}
            thirdPartyTextAriaLabel={linkedinAccount?.thirdPartyTextAriaLabel}
            thirdPartyTextType={linkedinAccount?.thirdPartyTextType}
            getThirdPartyScreenshotLoading={loading.getThirdPartyScreenshot}
            thirdPartyClickLoading={loading.thirdPartyClick}
            linkedinContracts={linkedinContracts}
            selectedContract={selectedContract}
            updateLoading={loading.update}
            handleVerificationCodeChange={handleVerificationCodeChange}
            handleVerifyLinkedinAccountFromAppSync={handleVerifyLinkedinAccountFromAppSync}
            handleImageClick={handleImageClick}
            handleDeleteMarker={handleDeleteMarker}
            handleSolveCaptcha={handleSolveCaptcha}
            handleThirdPartyClick={handleThirdPartyClick}
            handleThirdPartyTextChange={handleThirdPartyTextChange}
            handleSetThirdPartyTextFromAppSync={handleSetThirdPartyTextFromAppSync}
            handleSelectContract={handleSelectContract}
            handleAddContractFromAppSync={handleAddContractFromAppSync}
          />
        );
      case AppSyncSteps.SYNC_ERROR:
        return (
          <SyncError
            liProductType={liProductType}
            loading={loading.update}
            syncError={syncError}
            userName={userName}
            password={password}
            referenceLabel={createReferenceLabel(linkedinAccount)}
            handleUserNameChange={handleUserNameChange}
            handlePasswordChange={handlePasswordChange}
            handleEditAndSynchronizeLiAccount={handleEditAndSynchronizeLiAccount}
            handleReSynchronizeLiAccount={handleReSynchronizeLiAccount}
          />
        );
      case AppSyncSteps.SYNCHRONIZATION_SUCCESSFUL:
        return (
          <SyncSuccessful
            product={product}
            isStarterExperience={isStarterExperience}
            linkedinAccount={linkedinAccount}
            linkedinAccountFirstName={userFirstName}
            linkedinAccountEmail={userEmail}
            moveToCampaignBuilder={moveToCampaignBuilder}
            handleOpenEditLinkedinAccountNameDialog={handleOpenEditLinkedinAccountNameDialog}
            handleClose={handleClose}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'sm'} transitionDuration={0}>
      <Stack className={classes.header} gap={2.5}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
          <Stack direction={'row'} alignItems={'center'} gap={1.25}>
            <div className={classes.badge}>
              <LockOutlinedIcon className={classes.badgeIcon} />
            </div>
            <Typography className={classes.headerTitle}>
              {isLogoutMode
                ? t('appSyncLogoutHeader', 'LinkedIn-Konto abmelden')
                : t('appSyncConnectLinkedinAccount', 'LinkedIn-Konto verbinden')}
            </Typography>
          </Stack>
          <Stack direction={'row'} alignItems={'center'} gap={0.5}>
            {showProgress ? (
              <Typography className={classes.stepCounter}>
                {t('appSyncStepCounter', 'Schritt {{current}} von {{total}}', {
                  current: activeStepIndex + 1,
                  total: countedStepCount,
                })}
              </Typography>
            ) : null}
            {!isStarterExperience ? (
              <IconButton className={classes.closeButton} onClick={handleClose}>
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            ) : null}
          </Stack>
        </Stack>
        {showProgress ? <StepProgress totalSteps={countedStepCount} activeStep={activeStepIndex} /> : null}
      </Stack>
      <div className={classes.body}>
        {/* Neutral on purpose: never names the other workspace, company or person. */}
        {linkedinAccount?.connectedElsewhereAt && !isLogoutMode ? (
          <div className={classes.connectedElsewhere}>
            <StepHint
              tone={'warning'}
              icon={<InfoOutlinedIcon fontSize={'small'} />}
              text={t(
                'appSyncConnectedElsewhere',
                'Dieses LinkedIn-Konto ist bereits in einem anderen Workspace verbunden. Wird es an zwei Stellen gleichzeitig genutzt, meldet LinkedIn die Sitzungen ab. Bitte trenne es dort oder wende dich an den Support.',
              )}
            />
          </div>
        ) : null}
        {createContent()}
      </div>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
    width: '100%',
    maxWidth: '560px',
    maxHeight: 'calc(100dvh - 32px)',
    overflow: 'hidden',
  },
  connectedElsewhere: {
    padding: '16px 32px 0 32px',
    [theme.breakpoints.down('sm')]: {
      padding: '16px 20px 0 20px',
    },
  },
  header: {
    padding: '24px 32px 0 32px',
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      padding: '20px 20px 0 20px',
    },
  },
  badge: {
    width: '30px',
    height: '30px',
    borderRadius: '8px',
    background: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeIcon: {
    fontSize: theme.typography.pxToRem(17),
    color: '#FFF',
  },
  headerTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  stepCounter: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    whiteSpace: 'nowrap',
  },
  closeButton: {
    padding: '6px',
  },
  closeIcon: {
    color: theme.palette.primary.main,
  },
  body: {
    padding: '26px 32px 32px 32px',
    overflowY: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: '20px',
    },
  },
}));

export default AppSyncDialog;
